const calculateDatasetSums = (datasets, allowNegative) => {
  const positiveAllSums = []
  const negativeAllSums = []
  const dataLength = datasets[0].data.length

  for (let i = 0; i < dataLength; i++) {
    let positiveSum = 0
    let negativeSum = 0

    datasets.forEach((dataset) => {
      const value = Number(dataset.data[i]) || 0

      if (allowNegative) {
        if (value >= 0) {
          positiveSum += value
        } else {
          negativeSum += value
        }
      } else {
        positiveSum += Math.max(0, value)
      }
    })

    positiveAllSums.push(positiveSum)
    if (allowNegative) {
      negativeAllSums.push(negativeSum)
    }
  }

  return { positiveAllSums, negativeAllSums }
}

export const calculateStackedYAxisLimits = (
  datasets,
  allowNegative = false
) => {
  if (!datasets || !datasets.length) return { min: 0, max: 0, stepSize: 0 }

  const { positiveAllSums, negativeAllSums } = calculateDatasetSums(
    datasets,
    allowNegative
  )
  const maxValue = Math.max(...positiveAllSums)
  const minValue = allowNegative ? Math.min(...negativeAllSums) : 0

  const numberOfTicks = 6
  let step = maxValue / (numberOfTicks - 1)

  const magnitude = Math.pow(10, Math.floor(Math.log10(step)))
  const normalizedStep = step / magnitude

  if (normalizedStep < 1.5) step = magnitude
  else if (normalizedStep < 3) step = 2 * magnitude
  else if (normalizedStep < 7) step = 5 * magnitude
  else step = 10 * magnitude

  const roundedMax = Math.ceil(maxValue / step) * step
  const roundedMin =
    allowNegative && minValue < 0
      ? -(Math.ceil(Math.abs(minValue) / step) * step)
      : 0

  return {
    min: roundedMin,
    max: roundedMax,
    stepSize: step,
  }
}

export const calculateLineYAxisLimits = (datasets, allowNegative = false) => {
  if (!datasets || !datasets.length) return { min: 0, max: 0, stepSize: 0 }

  const allValues = extractAllValues(datasets)
  if (allValues.length === 0) return { min: 0, max: 0, stepSize: 0 }

  const maxValue = Math.max(...allValues)
  const minValue = allowNegative
    ? Math.min(...allValues)
    : Math.max(0, Math.min(...allValues))

  const { adjustedMax, adjustedMin } = calculateAdjustedLimits(
    maxValue,
    minValue,
    allowNegative
  )
  const step = calculateStepSize(adjustedMax, adjustedMin)
  const roundedMax = Math.ceil(adjustedMax / step) * step
  const roundedMin = calculateRoundedMin(
    minValue,
    adjustedMin,
    allowNegative,
    step
  )

  return {
    min: roundedMin,
    max: roundedMax,
    stepSize: step,
  }
}

const extractAllValues = (datasets) => {
  const allValues = []
  datasets.forEach((dataset) => {
    if (dataset.data && Array.isArray(dataset.data)) {
      dataset.data.forEach((value) => {
        const numValue = Number(value)
        if (!Number.isNaN(numValue)) {
          allValues.push(numValue)
        }
      })
    }
  })
  return allValues
}

const calculateAdjustedLimits = (maxValue, minValue, allowNegative) => {
  const range = maxValue - minValue

  if (range === 0) {
    return maxValue === 0
      ? { adjustedMax: 1, adjustedMin: 0 }
      : calculateEqualValuesLimits(maxValue, minValue, allowNegative)
  }

  return calculateRangeLimits(maxValue, minValue, range, allowNegative)
}

const calculateEqualValuesLimits = (maxValue, minValue, allowNegative) => {
  const artificialMargin = Math.abs(maxValue) * 0.2
  const adjustedMax = maxValue + artificialMargin
  const adjustedMin =
    allowNegative && minValue < 0
      ? minValue - artificialMargin
      : Math.max(0, minValue - artificialMargin)

  return { adjustedMax, adjustedMin }
}

const calculateRangeLimits = (maxValue, minValue, range, allowNegative) => {
  const margin = range * 0.1
  const adjustedMax = maxValue + margin
  const adjustedMin =
    allowNegative && minValue < 0
      ? minValue - margin
      : Math.max(0, minValue - margin)

  return { adjustedMax, adjustedMin }
}

const calculateStepSize = (adjustedMax, adjustedMin) => {
  const numberOfTicks = 6
  let step = (adjustedMax - adjustedMin) / (numberOfTicks - 1)

  if (step === 0) return 0.1

  const magnitude = Math.pow(10, Math.floor(Math.log10(step)))
  const normalizedStep = step / magnitude

  if (normalizedStep < 1.5) return magnitude
  if (normalizedStep < 3) return 2 * magnitude
  if (normalizedStep < 7) return 5 * magnitude
  return 10 * magnitude
}

const calculateRoundedMin = (minValue, adjustedMin, allowNegative, step) => {
  return allowNegative && minValue < 0 && adjustedMin < 0
    ? -(Math.ceil(Math.abs(adjustedMin) / step) * step)
    : 0
}

export const getChartOptions = (baseOptions, yAxisLimits, formatCallback) => {
  const defaultOptions = {
    scales: {
      yAxes: [
        {
          ticks: {},
        },
      ],
    },
  }

  const options = baseOptions || defaultOptions
  const scales = options.scales || defaultOptions.scales
  const yAxis =
    (scales.yAxes && scales.yAxes[0]) || defaultOptions.scales.yAxes[0]
  const ticks = yAxis.ticks || {}

  return {
    ...options,
    scales: {
      ...scales,
      yAxes: [
        {
          ...yAxis,
          ticks: {
            ...ticks,
            min: yAxisLimits.min,
            max: yAxisLimits.max,
            callback: formatCallback,
          },
        },
      ],
    },
  }
}

export const chartUtils = {
  calculateStackedYAxisLimits,
  getChartOptions,
}
